// Google reCAPTCHA v3 for every surface that renders a page.
//
// WHY THE CONFIG IS INJECTED, NOT FETCHED HERE. Only the host knows its own
// API base: the designer canvas has no backend at all, the published static
// site has the key baked into its manifest, and the Run view asks
// `/website/{owner}/{project}/captcha-config`. A context lets each of them
// answer for itself — and a host that provides nothing gets no check, which is
// exactly right for the designer canvas, where a submit is a rehearsal.
//
// WHY A TOKEN IS MINTED AT SUBMIT, NEVER AT MOUNT. A v3 token expires after
// two minutes and is spent the first time it is verified. Minting one when the
// form renders would hand the server an expired token from anybody who read
// the page before typing — which is most people filling in a real form.

import React, { createContext, useCallback, useContext } from 'react'

/** The four public doors, and the `action` each declares to Google. Google
 *  echoes the action back and the server compares, so a token minted on a
 *  cheap page cannot be spent on an expensive one. */
export type CaptchaSurface = 'forms' | 'auth' | 'transactions' | 'uploads'

export const CAPTCHA_ACTIONS: Record<CaptchaSurface, string> = {
  forms: 'form_submit',
  auth: 'account',
  transactions: 'txn_write',
  uploads: 'file_upload',
}

/** The header every protected call carries. Three of the four doors have a
 *  body shape that is not ours to extend (a multipart upload, a transaction
 *  payload, a batch save), so one header covers all of them. */
export const CAPTCHA_HEADER = 'X-Twasta-Captcha'

export interface PageCaptchaConfig {
  enabled: boolean
  provider?: string              // 'recaptcha_v3'
  siteKey?: string
  /** Which surfaces this project protects; absent = all of them. */
  surfaces?: CaptchaSurface[]
}

const PageCaptchaContext = createContext<PageCaptchaConfig>({ enabled: false })

export function PageCaptchaProvider(
  { config, children }: { config: PageCaptchaConfig; children: React.ReactNode },
) {
  return (
    <PageCaptchaContext.Provider value={config}>{children}</PageCaptchaContext.Provider>
  )
}

export function usePageCaptchaConfig(): PageCaptchaConfig {
  return useContext(PageCaptchaContext)
}

// ─── The Google script ──────────────────────────────────────────────────────

interface GreCaptcha {
  ready: (cb: () => void) => void
  execute: (siteKey: string, opts: { action: string }) => Promise<string>
}

declare global {
  interface Window { grecaptcha?: GreCaptcha }
}

const SCRIPT_ID = 'twasta-recaptcha-v3'
// One in-flight load per site key. Without it, a page holding three forms
// injects three <script> tags and races itself.
const loaders = new Map<string, Promise<GreCaptcha | null>>()

/** Load `api.js` for one site key, once. Resolves null (never rejects) when
 *  the script cannot load — an ad blocker, an offline visitor, a corporate
 *  proxy. That is not an error the page should show: the server decides what a
 *  missing token means, and for a form it means "store it and flag it". */
export function loadRecaptcha(siteKey: string): Promise<GreCaptcha | null> {
  if (!siteKey || typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(null)
  }
  const existing = loaders.get(siteKey)
  if (existing) return existing

  const promise = new Promise<GreCaptcha | null>((resolve) => {
    if (window.grecaptcha?.execute) { resolve(window.grecaptcha); return }

    const id = `${SCRIPT_ID}-${siteKey}`
    let script = document.getElementById(id) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = id
      script.async = true
      script.defer = true
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
      document.head.appendChild(script)
    }
    // A 10s ceiling, so a form whose submit is waiting on a script that will
    // never arrive still submits. Slow is a worse failure than unprotected
    // here — the visitor is staring at a spinner they did not ask for.
    const timer = window.setTimeout(() => resolve(null), 10_000)
    const settle = () => {
      window.clearTimeout(timer)
      resolve(window.grecaptcha?.execute ? window.grecaptcha : null)
    }
    script.addEventListener('load', settle, { once: true })
    script.addEventListener('error', () => { window.clearTimeout(timer); resolve(null) },
      { once: true })
    if (window.grecaptcha?.execute) settle()
  })

  loaders.set(siteKey, promise)
  return promise
}

/** Mint one token for one action, or null if the check cannot run. */
export async function executeRecaptcha(
  siteKey: string, action: string,
): Promise<string | null> {
  const grecaptcha = await loadRecaptcha(siteKey)
  if (!grecaptcha) return null
  try {
    return await new Promise<string | null>((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action })
          .then((token) => resolve(token || null))
          .catch(() => resolve(null))
      })
    })
  } catch {
    return null
  }
}

/**
 * `mint(surface)` for the current host's configuration — null whenever no
 * check applies, so a caller can always `await` it and pass the result along.
 */
export function usePageCaptcha() {
  const config = usePageCaptchaConfig()
  const mint = useCallback(async (surface: CaptchaSurface): Promise<string | null> => {
    if (!config.enabled || !config.siteKey) return null
    if (config.surfaces && !config.surfaces.includes(surface)) return null
    return executeRecaptcha(config.siteKey, CAPTCHA_ACTIONS[surface])
  }, [config.enabled, config.siteKey, config.surfaces])
  return { enabled: !!(config.enabled && config.siteKey), mint }
}

// ─── Attribution ────────────────────────────────────────────────────────────

/**
 * Google's terms require either their floating badge or this notice wherever
 * v3 runs. We hide the badge (it covers page content on a phone, and a
 * marketing page is a designed thing) and show the notice instead, which is
 * the alternative Google documents.
 */
export const RECAPTCHA_BADGE_CSS = '.grecaptcha-badge { visibility: hidden; }'

export function RecaptchaNotice({ style }: { style?: React.CSSProperties }) {
  return (
    <small
      style={{
        fontSize: 12, lineHeight: 1.5,
        color: 'var(--pg-text-secondary, var(--pg-muted, #64748b))',
        ...style,
      }}
    >
      Protected by reCAPTCHA — the Google{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer noopener"
         style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>
      {' '}and{' '}
      <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer noopener"
         style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</a>
      {' '}apply.
    </small>
  )
}
